import { createRng } from './rng';

/**
 * Peppol participant directory samples — used by:
 *   - Admin Console /tools "Peppol ID search"
 *   - Customer Portal NewInvoice "Buyer search" (when intake-form needs
 *     to resolve an external recipient before AS4 dispatch).
 *
 * UAE service-providers (SPs) appear under participant scheme `0088`
 * (GLN) and `0235` (UAE TRN). The Solara White-label AP (via TickStar)
 * shows up as "Solara Platform (UAE)".
 */

export type PeppolParticipantStatus = 'active' | 'inactive' | 'pending-onboarding';

export interface PeppolParticipant {
  participantId: string;
  legalName: string;
  legalNameAr: string;
  /** ISO country, mostly AE for the demo. */
  country: 'AE' | 'SA' | 'OM' | 'BH' | 'QA' | 'KW';
  /** Service provider hosting this participant. */
  serviceProvider: string;
  /** Capabilities the SP advertises in the directory. */
  capabilities: ('PINT-AE-INV' | 'PINT-AE-CRN' | 'TDD' | 'AS4-RECEIVE' | 'AS4-SEND')[];
  status: PeppolParticipantStatus;
  /** First registered in the directory. */
  registeredAt: string;
  /** Generic invoice metadata sample for UI. */
  contactEmail: string;
  /** Active SMP endpoint. */
  smpEndpoint: string;
}

const COMPANIES_AE: { en: string; ar: string }[] = [
  { en: 'Emaar Properties PJSC',          ar: 'إعمار العقارية ش.م.ع' },
  { en: 'DP World FZE',                    ar: 'موانئ دبي العالمية المنطقة الحرة' },
  { en: 'Etihad Airways',                  ar: 'الاتحاد للطيران' },
  { en: 'Aldar Properties',                ar: 'الدار العقارية' },
  { en: 'Mubadala Investment Co.',         ar: 'مبادلة للاستثمار' },
  { en: 'ADNOC Distribution',              ar: 'أدنوك للتوزيع' },
  { en: 'Etisalat by e&',                   ar: 'اتصالات من إي&' },
  { en: 'Damac Properties',                ar: 'داماك العقارية' },
  { en: 'Dubai Holding',                   ar: 'دبي القابضة' },
  { en: 'Jumeirah Group',                  ar: 'مجموعة جميرا' },
  { en: 'Al Futtaim Logistics LLC',         ar: 'الفطيم للخدمات اللوجستية ذ.م.م' },
  { en: 'Sharaf DG LLC',                    ar: 'شرف دي جي ذ.م.م' },
  { en: 'Tristar Engineering FZE',          ar: 'تريستار للهندسة المنطقة الحرة' },
  { en: 'Al Tayer Group',                   ar: 'مجموعة الطاير' },
  { en: 'Apparel Group LLC',                ar: 'مجموعة الملابس ذ.م.م' },
  { en: 'Spinneys Dubai',                   ar: 'سبينيس دبي' },
  { en: 'Lulu Hypermarket FZE',             ar: 'لولو هايبرماركت المنطقة الحرة' },
  { en: 'Aldar Engineering Services',       ar: 'الدار للخدمات الهندسية' },
  { en: 'Damac Maintenance Services',       ar: 'داماك لخدمات الصيانة' },
  { en: 'Mashreq Facilities Management',    ar: 'المشرق لإدارة المرافق' },
  { en: 'Khalid Al Rostamani Group',        ar: 'مجموعة خالد الرستماني' },
  { en: 'Galadari Brothers Group',          ar: 'مجموعة جلفار جالاداري' },
  { en: 'Easa Saleh Al Gurg Group',         ar: 'مجموعة عيسى صالح القرق' },
  { en: 'Al Naboodah Group Enterprises',    ar: 'مجموعة النابودة' },
  { en: 'Al Habtoor Group',                 ar: 'مجموعة الحبتور' },
  { en: 'Majid Al Futtaim Holding',         ar: 'ماجد الفطيم القابضة' },
  { en: 'Choithram Group',                  ar: 'مجموعة شويترام' },
  { en: 'Bin Dasmal Group',                 ar: 'مجموعة بن دسمل' },
  { en: 'NMC Trading',                      ar: 'إن إم سي للتجارة' },
  { en: 'Abdul Aziz Al Ghurair Group',      ar: 'مجموعة عبد العزيز الغرير' },
  { en: 'GEMS Education',                   ar: 'جيمس للتعليم' },
  { en: 'Bukhatir Group',                   ar: 'مجموعة بوخاطر' },
  { en: 'Al Maya Group',                    ar: 'مجموعة المايا' },
  { en: 'Lootah Group',                     ar: 'مجموعة لوتاه' },
  { en: 'Al Rais Holdings',                 ar: 'الرئيس القابضة' },
  { en: 'Al Tamimi & Company',              ar: 'التميمي ومشاركوه' },
  { en: 'Mashreq Bank PSC',                 ar: 'بنك المشرق ش.م.ع' },
  { en: 'Emirates NBD',                     ar: 'بنك الإمارات دبي الوطني' },
  { en: 'Abu Dhabi Commercial Bank',        ar: 'بنك أبوظبي التجاري' },
  { en: 'First Abu Dhabi Bank',             ar: 'بنك أبوظبي الأول' },
];

const COMPANIES_GULF: { en: string; ar: string; country: PeppolParticipant['country'] }[] = [
  { en: 'Saudi Aramco',           ar: 'أرامكو السعودية',         country: 'SA' },
  { en: 'STC Group',              ar: 'مجموعة الاتصالات السعودية', country: 'SA' },
  { en: 'Bank Muscat',            ar: 'بنك مسقط',                country: 'OM' },
  { en: 'Ooredoo Qatar',          ar: 'أوريدو قطر',              country: 'QA' },
  { en: 'BBK',                    ar: 'بنك البحرين والكويت',       country: 'BH' },
  { en: 'NBK',                     ar: 'بنك الكويت الوطني',         country: 'KW' },
  { en: 'Saudi Electricity Co.',   ar: 'الشركة السعودية للكهرباء',  country: 'SA' },
  { en: 'Stc Bahrain',             ar: 'إس تي سي البحرين',         country: 'BH' },
  { en: 'OQ',                       ar: 'أو كيو',                   country: 'OM' },
  { en: 'QatarEnergy',             ar: 'قطر للطاقة',               country: 'QA' },
];

const SP_BY_COUNTRY: Record<PeppolParticipant['country'], string[]> = {
  AE: ['Solara Platform (UAE)', 'Tabby SP', 'Emaratech Peppol', 'TallyPrime SP', 'Zoho UAE Peppol'],
  SA: ['ZATCA SP — TAQA', 'Sadad Peppol Provider'],
  OM: ['Tax Authority Oman SP'],
  BH: ['NBR Bahrain SP'],
  QA: ['GTA Qatar Peppol'],
  KW: ['Kuwait Peppol SP'],
};

function fakeTrn(rng: ReturnType<typeof createRng>): string {
  return `1${String(rng.int(0, 99_999_999_999_999)).padStart(14, '0')}`;
}

export function generatePeppolDirectory(count = 50, seed = 0x4_b_71): PeppolParticipant[] {
  const rng = createRng(seed);
  const out: PeppolParticipant[] = [];
  // Always include the Solara Platform (UAE) White-label AP as participant #1.
  out.push({
    participantId: '0088:JV-SOLARA-QVALIA-UAE',
    legalName: 'Solara Platform (UAE) — White-label Peppol AP',
    legalNameAr: 'منصة الجين (الإمارات) — نقطة وصول Peppol بالعلامة البيضاء',
    country: 'AE',
    serviceProvider: 'Solara Platform (UAE)',
    capabilities: ['PINT-AE-INV', 'PINT-AE-CRN', 'TDD', 'AS4-RECEIVE', 'AS4-SEND'],
    status: 'active',
    registeredAt: '2026-04-15T00:00:00.000Z',
    contactEmail: 'sp-ops@solara.ae',
    smpEndpoint: 'https://smp.solara.ae/businesscard/0088%3AJV-SOLARA-QVALIA-UAE',
  });

  let id = 1;
  while (out.length < count) {
    const isGulf = rng.chance(0.18) && id > 6;
    if (isGulf) {
      const co = rng.pick(COMPANIES_GULF);
      out.push({
        participantId: `0088:GLN-${String(rng.int(100_000_000, 999_999_999)).padStart(9, '0')}`,
        legalName: co.en,
        legalNameAr: co.ar,
        country: co.country,
        serviceProvider: rng.pick(SP_BY_COUNTRY[co.country]),
        capabilities: ['PINT-AE-INV', 'AS4-RECEIVE'],
        status: 'active',
        registeredAt: new Date(Date.now() - rng.int(30, 800) * 86400_000).toISOString(),
        contactEmail: `peppol@${co.en.toLowerCase().replace(/[^a-z]/g, '')}.com`,
        smpEndpoint: `https://smp.peppol.${co.country.toLowerCase()}/businesscard/0088%3A${id}`,
      });
    } else {
      const co = COMPANIES_AE[id % COMPANIES_AE.length]!;
      const r = rng.next();
      const status: PeppolParticipantStatus =
        r < 0.83 ? 'active' : r < 0.93 ? 'pending-onboarding' : 'inactive';
      const trn = fakeTrn(rng);
      out.push({
        participantId: rng.chance(0.6) ? `0235:AE-${trn}` : `0088:AETRN${trn}`,
        legalName: co.en,
        legalNameAr: co.ar,
        country: 'AE',
        serviceProvider: rng.pick(SP_BY_COUNTRY.AE),
        capabilities: rng.chance(0.7)
          ? ['PINT-AE-INV', 'PINT-AE-CRN', 'TDD', 'AS4-RECEIVE', 'AS4-SEND']
          : ['PINT-AE-INV', 'AS4-RECEIVE'],
        status,
        registeredAt: new Date(Date.now() - rng.int(7, 720) * 86400_000).toISOString(),
        contactEmail: `peppol@${co.en.toLowerCase().replace(/[^a-z]/g, '')}.ae`,
        smpEndpoint: `https://smp.solara.ae/businesscard/0088%3AAETRN${trn}`,
      });
    }
    id++;
  }
  return out;
}

export function searchPeppolDirectory(
  list: PeppolParticipant[],
  query: string,
  locale: 'en-AE' | 'ar-AE' | 'hu-HU' = 'en-AE',
): PeppolParticipant[] {
  const q = query.trim().toLowerCase();
  if (!q) return list.slice(0, 25);
  return list.filter((p) => {
    const hay = locale === 'ar-AE' ? p.legalNameAr : p.legalName;
    return (
      p.participantId.toLowerCase().includes(q) ||
      hay.toLowerCase().includes(q) ||
      p.serviceProvider.toLowerCase().includes(q) ||
      p.country.toLowerCase() === q
    );
  });
}
